import type {
  CatalogResponse,
  LeaderboardEntry,
  ScoreSubmission,
  SubmittedScore,
} from "./types";

const RAW_API = (import.meta.env.VITE_API_URL as string | undefined) ?? "/api";
// Strip trailing slash so we can safely concatenate paths.
const API = RAW_API.replace(/\/$/, "");

const LOCAL_BUNDLED_CATALOG_URL = "/catalog.json";
const COUNTRY_CACHE_KEY = "user_country_v1";

/**
 * Detect the player's country (ISO 3166-1 alpha-2) for regional catalog
 * filtering. Cached for the session so we don't re-hit the IP API. Returns
 * null if detection fails -- the backend then returns the global catalog.
 */
async function detectCountry(): Promise<string | null> {
  try {
    const cached = sessionStorage.getItem(COUNTRY_CACHE_KEY);
    if (cached === "none") return null;
    if (cached && /^[A-Z]{2}$/.test(cached)) return cached;
  } catch {
    // sessionStorage unavailable (private mode etc.) -- just refetch.
  }
  try {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), 2000);
    const r = await fetch("https://api.country.is/", { signal: controller.signal });
    clearTimeout(timer);
    if (!r.ok) throw new Error("country lookup failed");
    const data = (await r.json()) as { country?: string };
    const cc = (data.country || "").toUpperCase();
    const valid = /^[A-Z]{2}$/.test(cc) ? cc : null;
    try {
      sessionStorage.setItem(COUNTRY_CACHE_KEY, valid ?? "none");
    } catch {
      // ignore -- not critical
    }
    return valid;
  } catch {
    return null;
  }
}

/**
 * Build a same-origin URL for a mushroom image. The backend returns either:
 *   - "/mushrooms/<id>.jpg"          (local dev, static asset)
 *   - "https://cdn.../mushrooms/<id>.jpg"  (prod, CloudFront)
 * Both are fine to pass straight to <img src>.
 */
export async function fetchCatalog(): Promise<CatalogResponse> {
  const country = await detectCountry();
  const qs = country ? `?country=${country}` : "";
  try {
    const r = await fetch(`${API}/mushrooms${qs}`);
    if (r.ok) return (await r.json()) as CatalogResponse;
  } catch {
    // fall through
  }
  // Offline / no-backend fallback: load the static catalog snapshot.
  const r = await fetch(LOCAL_BUNDLED_CATALOG_URL);
  if (!r.ok) throw new Error("Could not load mushroom catalog");
  return (await r.json()) as CatalogResponse;
}

export async function fetchLeaderboard(
  limit = 20,
): Promise<LeaderboardEntry[]> {
  try {
    const r = await fetch(`${API}/leaderboard?limit=${limit}`);
    if (!r.ok) return [];
    const data = (await r.json()) as { scores: LeaderboardEntry[] };
    return data.scores ?? [];
  } catch {
    return [];
  }
}

export async function submitScore(
  submission: ScoreSubmission,
): Promise<SubmittedScore | null> {
  try {
    const r = await fetch(`${API}/scores`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(submission),
    });
    if (!r.ok) return null;
    return (await r.json()) as SubmittedScore;
  } catch {
    return null;
  }
}
